import { ColDef, ColType } from '../interfaces/colDef';
import { ORG_STATUS } from '../shared/platform-overview.service';

/**
 * Column definitions for the platform overview table. Consumed by
 * bst-dual-axis via the DualAxisDelegate contract.
 *
 * The Organization column is sticky-left (always visible / not hidable);
 * everything else is standard and can be toggled in the Options pane.
 *
 * Titles are ngx-translate keys (lowercase nouns), matching the
 * coldefs-user.ts convention. Keys not yet in the i18n bundle render as
 * the key text via the missing-translation fallback (acceptable while
 * the page is internal-only English).
 */

export const stickyColumns: ColDef[] = [
  {
    name: 'organization',
    title: 'organization',
    type: ColType.STRING,
    sort: 'client_name',
    // Not filterable: the only backed filter here is `status` (otherDefs); the table
    // fetches all rows and filters client-side by status only. `filter: false` keeps this
    // sticky column out of the dual-axis funnel + Table-options FILTER section (which now
    // includes sticky columns), so it can't surface a no-op filter control.
    filter: false,
    required: true,
    disabled: true,
  },
];

export const columns: ColDef[] = [
  {
    name: 'active_profile_count',
    title: 'profiles',
    type: ColType.INT,
    sort: true,
    filter: false,
    required: false,
  },
  {
    name: 'profiles_trend',
    title: 'trend',
    type: ColType.STRING,
    sort: false,
    filter: false,
    required: false,
  },
  {
    name: 'logged_in_users',
    title: 'loggedinusers',
    type: ColType.INT,
    sort: true,
    filter: false,
    required: false,
  },
  {
    name: 'storage_bytes',
    title: 'storage',
    type: ColType.INT,
    sort: true,
    filter: false,
    required: false,
  },
  {
    name: 'matching_events',
    title: 'matching',
    type: ColType.INT,
    sort: true,
    filter: false,
    required: false,
  },
  {
    name: 'last_login_at',
    title: 'lastlogin',
    type: ColType.DATE,
    sort: true,
    filter: false,
    required: false,
  },
  {
    name: 'intake_enabled',
    title: 'platform.overview.intake.col_enabled',
    type: ColType.STRING,
    sort: false,
    filter: false,
    required: false,
  },
  {
    name: 'intake_address_masked',
    title: 'platform.overview.intake.col_address',
    type: ColType.STRING,
    sort: false,
    filter: false,
    required: false,
  },
  {
    name: 'intake_month_mails',
    title: 'platform.overview.intake.col_month',
    type: ColType.INT,
    sort: true,
    filter: false,
    required: false,
  },
  {
    name: 'intake_monthly_cap',
    title: 'platform.overview.intake.col_cap',
    type: ColType.INT,
    sort: false,
    filter: false,
    required: false,
  },
  {
    name: 'intake_held',
    title: 'platform.overview.intake.col_held',
    type: ColType.INT,
    sort: true,
    filter: false,
    required: false,
  },
  {
    name: 'quotes_enabled',
    title: 'platform.overview.quotes.col_enabled',
    type: ColType.STRING,
    sort: false,
    filter: false,
    required: false,
  },
];

/**
 * "Other" columns surfaced in the Options pane as filter controls
 * (not as table columns). IDs are derived from ORG_STATUS so the
 * frontend can't drift from the backend enum; display labels follow
 * the project convention (Capitalized, see coldefs-user.ts / coldefs-client.ts).
 */
export const otherDefs: ColDef[] = [
  {
    name: 'status',
    title: 'status',
    type: ColType.RADIO,
    required: false,
    list: [
      { id: 'all', name: 'All' },
      ...ORG_STATUS.map(s => ({ id: s, name: s.charAt(0).toUpperCase() + s.slice(1) })),
    ],
  },
];
